import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/design-system/lib/utils";

/**
 * Pill-shaped count chip — distinct from the design-system Badge (which is a
 * rectangular status pill). Use this for unread counts, queue depths, badge
 * indicators on icons, etc. Caps at "99+" so the chip width stays bounded.
 *
 * Pulls from existing palette tokens (gold / status-error / onyx / white-pure)
 * — no new colors. Shared so notifications, tasks, etc. can adopt without
 * each one re-inlining the same span.
 */

const countBadgeVariants = cva(
  "inline-flex items-center justify-center shrink-0 " +
    "rounded-pill font-display font-bold tracking-label whitespace-nowrap",
  {
    variants: {
      tone: {
        // Inbox row, conversation tile — high-contrast on a light surface.
        gold:  "bg-gold text-onyx",
        // Navbar dot — high-contrast on the dark top bar; intentionally
        // attention-grabbing because the badge is the only cue a user gets
        // when there's no email/push notification.
        error: "bg-status-error text-white-pure",
      },
      size: {
        // Both chip sizes used in the messaging UI today; navbar uses sm,
        // inbox row uses md. Default md.
        sm: "min-w-[18px] h-[18px] px-1 text-[10px]",
        md: "min-w-[20px] h-[20px] px-[6px] text-[10px]",
      },
    },
    defaultVariants: { tone: "gold", size: "md" },
  },
);

export interface CountBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof countBadgeVariants> {
  /** The number to render. Values > 99 collapse to "99+". */
  count: number;
  /** Cap text used when count exceeds 99. Defaults to "99+". */
  cap?: string;
}

export function CountBadge({ count, cap = "99+", tone, size, className, ...props }: CountBadgeProps) {
  if (count <= 0) return null;
  return (
    <span className={cn(countBadgeVariants({ tone, size }), className)} {...props}>
      {count > 99 ? cap : count}
    </span>
  );
}
